import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import "./HomePage.css"; // reuse the same CSS for animated text

export default function PrivacyPolicyPage() {
  return (
    <Box marginTop={4}>
      <Stack
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 3, md: 5 }}
        paddingX={0}
      >
        {/* Main heading – same animated style as the HomePage title */}
        <Typography
          className="animate-character"
          variant={"h3"}
          fontWeight="bold"
          align="center"
          sx={{
            "@media (max-width: 600px)": {
              fontSize: "2.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 601px) and (max-width: 900px)": {
              fontSize: "3.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 901px)": {
              fontSize: "4rem",
              letterSpacing: 0.5,
            },
          }}
        >
          Privacy Policy
        </Typography>

        {/* Decorative dividers like on the home page */}
        <Box sx={{ width: "100%" }}>
          <Divider />
          <Divider />
        </Box>

        {/* Content container – max width for better readability */}
        <Box
          sx={{
            width: { xs: "95%", md: "80%" },
            maxWidth: "900px",
            textAlign: "left",
          }}
        >
          {/* Information We Collect */}
          <Divider sx={{ width: "100%", mb: 2, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Information We Collect
            </Typography>
          </Divider>
          <Typography paragraph>
            To provide you with our services, we collect the following
            information:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Phone number – used for OTP‑based login and authentication.</li>
            <li>
              Email address – used for account‑related communications,
              password recovery, and important updates.
            </li>
            <li>
              Profile image – if you choose to upload one, it is stored to
              personalise your account experience.
            </li>
          </Typography>

          {/* How We Use Data */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              How We Use Your Data
            </Typography>
          </Divider>
          <Typography paragraph>
            Your personal data is used exclusively to:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Create and manage your account.</li>
            <li>
              Provide access to audiobooks and subscription features you have
              purchased.
            </li>
            <li>Improve our services, troubleshoot issues, and analyse usage.</li>
            <li>Communicate important service announcements.</li>
          </Typography>
          <Typography paragraph>
            We never sell your personal data to third parties.
          </Typography>

          {/* Data Storage */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Data Storage & Security
            </Typography>
          </Divider>
          <Typography paragraph>
            All data is stored securely on AWS (Amazon Web Services) servers.
            We implement industry‑standard security measures, including
            encryption in transit and at rest, to protect your information from
            unauthorised access.
          </Typography>

          {/* Third‑Party Services */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Third‑Party Services
            </Typography>
          </Divider>
          <Typography paragraph>
            Payments for subscriptions are processed securely by{" "}
            <strong>Razorpay</strong>. We do not store your payment card
            details – they are handled directly by Razorpay in compliance with
            PCI‑DSS standards. We may also use third‑party analytics tools
            (such as Google Analytics) to understand how our services are used;
            these tools only collect anonymised usage data.
          </Typography>

          {/* User Rights */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Your Rights
            </Typography>
          </Divider>
          <Typography paragraph>
            You have the right to:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate or incomplete information.</li>
            <li>Request deletion of your account and associated data.</li>
            <li>Withdraw consent for data processing at any time.</li>
          </Typography>
          <Typography paragraph>
            To exercise these rights, please contact us using the email below.
          </Typography>

          {/* Cookies */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Cookies & Tracking
            </Typography>
          </Divider>
          <Typography paragraph>
            We use essential cookies to authenticate users, maintain session
            state, and remember your preferences. You may disable cookies in
            your browser settings, but this may affect the functionality of the
            site.
          </Typography>

          {/* Children's Privacy */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Children's Privacy
            </Typography>
          </Divider>
          <Typography paragraph>
            Our services are not directed to individuals under the age of 13.
            We do not knowingly collect personal information from children. If
            you believe a child has provided us with personal data, please
            contact us so we can delete it.
          </Typography>

          {/* Changes to This Policy */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Changes to This Privacy Policy
            </Typography>
          </Divider>
          <Typography paragraph>
            We may update this policy from time to time. Any changes will be
            posted on this page with an updated “Last Revised” date. We
            encourage you to review this page periodically.
          </Typography>
          <Typography paragraph>
            <em>Last Revised: 28 March 2026</em>
          </Typography>

          {/* Contact */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Contact Us
            </Typography>
          </Divider>
          <Typography paragraph>
            If you have any questions about this Privacy Policy or your
            personal data, please reach out to us at:
            <br />
            <strong>Email:</strong> info@sainiaudiobooks.com
          </Typography>
        </Box>

        {/* Back to Home button – consistent with HomePage button styles */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={{
            px: 4,
            py: 1.75,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 9999,
            background: "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
            boxShadow:
              "0 6px 18px rgba(0,114,255,0.3), 0 2px 6px rgba(0,0,0,0.1)",
            textTransform: "none",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 10px 24px rgba(0,114,255,0.4), 0 4px 10px rgba(0,0,0,0.12)",
              background:
                "linear-gradient(90deg, #0062e6 0%, #00b6ff 100%) !important",
            },
            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
          component={Link}
          to="/"
          aria-label="Return to homepage"
        >
          ← Return to Home
        </Button>

        {/* Bottom decorative dividers */}
        <Box sx={{ width: "100%", mt: 4 }}>
          <Divider />
          <Divider />
        </Box>
      </Stack>
    </Box>
  );
}